import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import SupporterButton from './SupporterButton';
import AuthModal from '../../../components/feature/AuthModal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Stream {
  id: number;
  title: string;
  streamer: string;
  avatar: string;
  viewers: number;
  thumbnail: string;
  category: string;
  duration: string;
}

export default function LiveStreams() {
  const [streams, setStreams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const fetchStreams = async () => {
      const { data, error } = await supabase
        .from('streams')
        .select(`
          *,
          profiles!streams_streamer_id_fkey (
            username,
            avatar_url
          )
        `)
        .eq('is_live', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Error fetching streams:', error);
      } else {
        setStreams(data || []);
      }
      setLoading(false);
    };

    fetchStreams();
  }, []);

  return (
    <>
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Live Now</h2>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-red-500 font-semibold">{streams.length} Live</span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line text-gray-600 text-5xl animate-spin"></i>
            </div>
          ) : streams.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-live-line text-gray-600 text-5xl mb-4"></i>
              <p className="text-gray-400">No live streams at the moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {streams.map((stream) => (
                <div key={stream.id} className="bg-gray-800 rounded-xl overflow-hidden hover:transform hover:scale-105 transition-all">
                  <div className="relative">
                    <img
                      src={stream.thumbnail_url}
                      alt={stream.title}
                      className="w-full h-48 object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      LIVE
                    </div>
                    <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-sm font-semibold">
                      <i className="ri-eye-line mr-1"></i>
                      {stream.viewer_count}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <img
                        src={stream.profiles?.avatar_url}
                        alt={stream.profiles?.username}
                        className="w-10 h-10 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold mb-1 line-clamp-2">{stream.title}</h3>
                        <p className="text-gray-400 text-sm truncate">{stream.profiles?.username}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <span className="text-teal-400 text-sm font-semibold truncate">{stream.game_category}</span>
                      <SupporterButton 
                        streamerId={stream.streamer_id} 
                        streamerName={stream.profiles?.username}
                        onAuthRequired={() => setShowAuthModal(true)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} defaultMode="signup" />}
    </>
  );
}
